import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../Components/Firebase";
import SapakimHead from "../Sapakim/SapakimHead";
import "../Styles/Sapakim/SQuotations.css";
import Loading from "../Components/Loading";

// Digital Signature Modal Component
// Digital Signature Modal Component with Mobile Support
const SignatureModal = ({ isOpen, onClose, onSave, quotationId }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [isOpen]);

  // Get coordinates for both mouse and touch events
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Check if it's a touch event
    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    // Mouse event
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault(); // Prevent scrolling on touch devices
    setIsDrawing(true);
    setIsEmpty(false);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault(); // Prevent scrolling on touch devices
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  const saveSignature = () => {
    if (isEmpty) {
      alert('אנא הוסף חתימה לפני השמירה');
      return;
    }
    
    const canvas = canvasRef.current;
    const signatureDataURL = canvas.toDataURL('image/png');
    onSave(quotationId, signatureDataURL);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '10px',
        width: '500px',
        maxWidth: '90vw'
      }}>
        <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>
          חתימה דיגיטלית לאישור הצעת מחיר
        </h3>
        
        <div style={{
          border: '2px solid #ccc',
          borderRadius: '8px',
          marginBottom: '20px',
          touchAction: 'none' // Prevent default touch behaviors
        }}>
          <canvas
            ref={canvasRef}
            width={460}
            height={200}
            style={{
              display: 'block',
              cursor: 'crosshair',
              backgroundColor: '#fafafa',
              touchAction: 'none' // Prevent scrolling and zooming
            }}
            // Mouse events
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            // Touch events for mobile
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            onTouchCancel={stopDrawing}
          />
        </div>
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '10px',
          flexWrap: 'wrap' // Allow buttons to wrap on small screens
        }}>
          <button
            onClick={clearSignature}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              minWidth: '100px'
            }}
          >
            נקה חתימה
          </button>
          
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                minWidth: '80px'
              }}
            >
              ביטול
            </button>
            
            <button
              onClick={saveSignature}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                minWidth: '120px'
              }}
            >
              אישור וחתימה
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function SQuotations() {
  const [quotations, setQuotations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [pendingApprovalId, setPendingApprovalId] = useState(null);
  const navigate = useNavigate();
  const { UID } = useParams();

  useEffect(() => {
  const fetchQuotationsAndCustomers = async () => {
    try {
      if (!UID) return;

      const q = query(collection(db, "Quotations"), where("SapakId", "==", UID));
      const querySnapshot = await getDocs(q);

      // Get quotation documents and sort by orderNumber (ascending)
      let quotationData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by orderNumber ascending
      quotationData.sort((a, b) => {
        const numA = parseInt(a.orderNumber) || 0;
        const numB = parseInt(b.orderNumber) || 0;
        return numA - numB;
      });

      // Fetch matching customers
      const custQuery = query(collection(db, "Customers"), where("SapakId", "==", UID));
      const custSnapshot = await getDocs(custQuery);
      const customersList = custSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCustomers(customersList);

      // Add customer name to each quotation
      const quotationsWithCustomerNames = await Promise.all(
        quotationData.map(async (quotation) => {
          let customerName = "—";
          if (quotation.CustomerID) {
            const customerDoc = await getDoc(doc(db, "Customers", quotation.CustomerID));
            if (customerDoc.exists()) {
              customerName = customerDoc.data().Name || "—";
            }
          }
          return {
            ...quotation,
            customerName,
          };
        })
      );

      setQuotations(quotationsWithCustomerNames);
      setLoading(false);
    } catch (error) {
      console.error("שגיאה בשליפת הצעות המחיר:", error);
    }
  };

  fetchQuotationsAndCustomers();
}, [UID]);


  const handleCustomerChange = async (quotationId, selectedCustomerId) => {
    try {
      const quotationRef = doc(db, "Quotations", quotationId);
      await updateDoc(quotationRef, {
        CustomerID: selectedCustomerId,
      });

      const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);
      setQuotations((prev) =>
        prev.map((q) =>
          q.id === quotationId
            ? { ...q, CustomerID: selectedCustomerId, customerName: selectedCustomer?.Name || "—" }
            : q
        )
      );
    } catch (error) {
      console.error("שגיאה בעדכון לקוח בהצעת המחיר:", error);
    }
  };

  const handleApprovalClick = (quotationId) => {
    setPendingApprovalId(quotationId);
    setShowSignatureModal(true);
  };

  const handleSignatureSave = async (quotationId, signatureDataURL) => {
    try {
      const quotationRef = doc(db, "Quotations", quotationId);
      await updateDoc(quotationRef, {
        QStatus: "approved",
        signature: signatureDataURL,
        approvalDate: new Date(),
      });

      setQuotations((prev) =>
        prev.map((q) =>
          q.id === quotationId 
            ? { 
                ...q, 
                QStatus: "approved", 
                signature: signatureDataURL,
                approvalDate: new Date()
              } 
            : q
        )
      );

      setShowSignatureModal(false);
      setPendingApprovalId(null);
      alert('הצעת המחיר אושרה בהצלחה עם חתימה דיגיטלית!');
    } catch (error) {
      console.error("שגיאה בעדכון הסטטוס עם חתימה:", error);
      alert('שגיאה בשמירת החתימה. אנא נסה שוב.');
    }
  };

  const handleStatusUpdate = async (quotationId, newStatus) => {
    if (newStatus === "approved") {
      // For approval, open signature modal instead of direct update
      handleApprovalClick(quotationId);
      return;
    }

    try {
      const quotationRef = doc(db, "Quotations", quotationId);
      await updateDoc(quotationRef, {
        QStatus: newStatus,
      });

      setQuotations((prev) =>
        prev.map((q) =>
          q.id === quotationId ? { ...q, QStatus: newStatus } : q
        )
      );
    } catch (error) {
      console.error(`שגיאה בעדכון הסטטוס ל-${newStatus}:`, error);
    }
  };

  const getStatusColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "pending":
        return "orange";
      case "rejected":
        return "red";
      case "approved":
        return "green";
      default:
        return "gray";
    }
  };

  const filteredQuotations = quotations.filter((q) => {
    const matchesSearch =
      (q.orderNumber && q.orderNumber.toString().includes(searchTerm)) ||
      (q.customerName && q.customerName.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus =
      statusFilter === "all" ||
      (q.QStatus && q.QStatus.toLowerCase() === statusFilter.toLowerCase());

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <SapakimHead />
      <div className="quotations-container">
        <h2>הצעות מחיר </h2>

        <div className="top-bar" style={{ alignItems: "center" }}>
          <input
            type="text"
            className="search-input"
            placeholder="חיפוש לפי מספר הזמנה או שם לקוח..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: "300px" }}
          />

          <select
            className="search-input"
            style={{ maxWidth: "180px" }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="סינון לפי סטטוס"
          >
            <option value="all">כל הסטטוסים</option>
            <option value="pending">בהמתנה</option>
            <option value="approved">מאושר</option>
            <option value="rejected">נדחה</option>
          </select>

          <button
            className="new-quotation-button"
            onClick={() => navigate(`/SapakimHome/${UID}/SQuotations/SCreateNewQuotation`)}
            style={{ minWidth: "200px" }}
          >
            בקשת הצעת מחיר חדשה
          </button>
        </div>

        {filteredQuotations.length === 0 ? (
          <p>אין הצעות מחיר להצגה</p>
        ) : (
          <table className="quotations-table">
            <thead>
              <tr>
                <th>מספר</th>
                <th>סטטוס</th>
                <th>מספר הזמנה</th>
                <th>תאריך יצירה</th>
                <th>שם לקוח</th>
                <th>חתימה</th>
                <th>פעולה</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotations.map((q, index) => {
                const isPending = (q.QStatus || "").toLowerCase() === "pending";
                const isApproved = (q.QStatus || "").toLowerCase() === "approved";
                return (
                  <tr key={q.id}>
                    <td data-label="מספר">{index + 1}</td>
                    <td data-label="סטטוס">
                      <div
                        title={q.QStatus || "לא ידוע"}
                        style={{
                          width: "14px",
                          height: "14px",
                          borderRadius: "50%",
                          backgroundColor: getStatusColor(q.QStatus),
                          margin: "0 auto",
                        }}
                      />
                    </td>
                    <td data-label="מספר הזמנה">{q.orderNumber || "—"}</td>
                    <td data-label="תאריך יצירה">
                      {q.createdAt && typeof q.createdAt.toDate === "function"
                        ? q.createdAt.toDate().toLocaleString()
                        : "תאריך לא זמין"}
                    </td>
                    <td data-label="שם לקוח">
                      {q.customerName && q.customerName !== "—" ? (
                        q.customerName
                      ) : (
                        <select
                          aria-label="בחירת לקוח"
                          onChange={(e) => handleCustomerChange(q.id, e.target.value)}
                          defaultValue=""
                        >
                          <option value="" disabled>
                            בחר לקוח
                          </option>
                          {customers.map((customer) => (
                            <option key={customer.id} value={customer.id}>
                              {customer.Name}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td data-label="חתימה">
                      {isApproved && q.signature ? (
                        <img 
                          src={q.signature} 
                          alt="חתימה דיגיטלית" 
                          style={{
                            width: "50px",
                            height: "25px",
                            objectFit: "contain",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            cursor: "pointer"
                          }}
                          title="לחץ לצפייה בחתימה המלאה"
                          onClick={() => window.open(q.signature, '_blank')}
                        />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td
                      data-label="פעולה"
                      style={{ display: "flex", gap: "8px", justifyContent: "center" }}
                    >
                      <button
                        onClick={() => navigate(`/SapakimHome/${UID}/SQuotations/${q.CustomerID}/${q.id}`)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "6px",
                          border: "none",
                          cursor: "pointer",
                          backgroundColor: "#007bff",
                          color: "#fff",
                          fontWeight: "600",
                        }}
                      >
                        הצג
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(q.id, "approved")}
                        disabled={!isPending}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "6px",
                          border: "none",
                          cursor: isPending ? "pointer" : "not-allowed",
                          backgroundColor: isPending ? "green" : "#a0a0a0",
                          color: "#fff",
                          fontWeight: "600",
                        }}
                        title="אישור עם חתימה"
                      >
                        אישור
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(q.id, "rejected")}
                        disabled={!isPending}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "6px",
                          border: "none",
                          cursor: isPending ? "pointer" : "not-allowed",
                          backgroundColor: isPending ? "red" : "#a0a0a0",
                          color: "#fff",
                          fontWeight: "600",
                        }}
                        title="דחייה"
                      >
                        דחייה
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <SignatureModal
        isOpen={showSignatureModal}
        onClose={() => {
          setShowSignatureModal(false);
          setPendingApprovalId(null);
        }}
        onSave={handleSignatureSave}
        quotationId={pendingApprovalId}
      />
    </>
  );
}

export default SQuotations;